import React from 'react';
import {
  Card, CardContent, Box, Typography, IconButton, Collapse, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface QuizQuestionProps {
  question: string;
  answer: string;
  idx: number;
  revealed: boolean;
  markedCorrect: boolean | null;
  showValidation: boolean;
  onReveal: (idx: number) => void;
  onMark: (idx: number, value: boolean) => void;
  pastel: string;
}

const QuizQuestion: React.FC<QuizQuestionProps> = ({
  question, answer, idx, revealed, markedCorrect, showValidation, onReveal, onMark, pastel
}) => (
  <Card
    variant="outlined"
    sx={{
      mb: 3,
      background: pastel,
      borderRadius: 3,
      boxShadow: 2,
      transition: 'background 0.3s, border-color 0.3s',
      borderColor:
        showValidation && markedCorrect === null
          ? '#b71c1c'
          : 'rgba(0,0,0,0.12)',
      borderWidth: showValidation && markedCorrect === null ? 2 : 1,
    }}
  >
    <CardContent>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => onReveal(idx)}
        sx={{ cursor: 'pointer' }}
      >
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          {question}
          {markedCorrect === true && (
            <Box sx={{ ml: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }}>
              <CheckCircleIcon sx={{ color: '#4ade80' }} />
            </Box>
          )}
          {markedCorrect === false && (
            <Box sx={{ ml: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }}>
              <CancelIcon sx={{ color: '#b71c1c' }} />
            </Box>
          )}
        </Typography>
        <IconButton size="small">
          {revealed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={revealed} timeout={500}>
        <Box mt={2} sx={{ background: '#fffbe6', borderRadius: 2, p: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              textAlign: 'center',
              color:
                markedCorrect === true
                  ? '#34c759'
                  : markedCorrect === false
                  ? '#b71c1c'
                  : 'inherit',
              transition: 'color 0.4s',
            }}
          >
            {answer}
          </Typography>
          <Box display="flex" justifyContent="center">
            <ToggleButtonGroup
              exclusive
              value={markedCorrect}
              onChange={(_, value) => {
                if (value !== null) onMark(idx, value);
              }}
              sx={{ mt: 2 }}
            >
              <ToggleButton
                value={true}
                sx={{
                  fontWeight: 700,
                  px: 3,
                  color: '#4ade80',
                  borderColor: '#4ade80',
                  '&.Mui-selected': {
                    background: '#eafff3',
                    color: '#219150'
                  }
                }}
              >
                Correct
              </ToggleButton>
              <ToggleButton
                value={false}
                color="error"
                sx={{
                  fontWeight: 700,
                  px: 3,
                  '&.Mui-selected': {
                    background: '#ffeaea',
                    color: '#b71c1c',
                    borderColor: '#b71c1c'
                  }
                }}
              >
                Incorrect
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Collapse>
    </CardContent>
  </Card>
);

export default QuizQuestion;